/**
 * GeneralSystemGuide — Overview of the entire Eyvo eProcurement platform.
 * Covers login, navigation, dashboard, and a brief introduction to each module.
 */
import React from 'react';
import GuideViewer, { GuideSection, GuideSubSection, GuideCallout, GuideFieldTable, GuideTOC } from './GuideViewer';
import AppShell from '../../components/layout/AppShell';

function GuideContent() {
  const toc = [
    { id: 'introduction', label: 'Introduction to Eyvo' },
    { id: 'logging-in', label: 'Logging In' },
    { id: 'navigation', label: 'Navigating the Application' },
    { id: 'dashboard', label: 'The Dashboard' },
    { id: 'command-center', label: 'Command Center' },
    { id: 'data-files', label: 'Data Files (Master Data)' },
    { id: 'account-codes', label: 'Account Codes' },
    { id: 'modules-overview', label: 'Core Modules Overview' },
    { id: 'reports', label: 'Reporting & Exports' },
    { id: 'system-admin', label: 'System Administration' },
    { id: 'timezone', label: 'Timezone & Date Display' },
    { id: 'accessibility', label: 'Accessibility' },
    { id: 'support', label: 'Getting Help' },
  ];

  return (
    <GuideViewer title="Eyvo eProcurement" subtitle="General System User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="introduction" title="1. Introduction to Eyvo">
        <p className="text-sm text-slate-600 mb-3">
          Eyvo is an enterprise-grade electronic procurement (eProcurement) platform designed to streamline and control your organisation's purchasing processes from end to end. It covers the complete procure-to-pay lifecycle including requisitioning, sourcing, purchase orders, goods receiving, invoice matching, contract management, inventory control, and asset management.
        </p>
        <p className="text-sm text-slate-600 mb-3">
          The platform is multi-tenant, meaning each organisation operates within its own isolated environment with its own data, users, settings, and branding. All dates and times throughout the system are displayed in your organisation's configured timezone.
        </p>
        <GuideSubSection title="Key Capabilities">
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li><strong>Purchase Requisitions</strong> — Create, route, and approve purchase requests before they become orders</li>
            <li><strong>RFx Sourcing</strong> — Run RFPs, RFQs, RFIs, and live reverse auctions to find the best suppliers</li>
            <li><strong>Purchase Orders</strong> — Issue formal orders to suppliers with multi-level approval workflows</li>
            <li><strong>Contracts</strong> — Manage supplier agreements with version control, clauses, and renewal tracking</li>
            <li><strong>Receiving</strong> — Record delivery receipts and match them against purchase orders</li>
            <li><strong>Invoice Matching</strong> — Three-way match invoices against orders and receipts</li>
            <li><strong>Inventory Control</strong> — Track stock levels, batches, serial numbers, and run cycle counts</li>
            <li><strong>Asset Management</strong> — Register, depreciate, and track fixed assets through their lifecycle</li>
            <li><strong>Supplier Portal</strong> — Self-service portal where suppliers view orders, submit invoices, and respond to sourcing events</li>
            <li><strong>Reporting & Analytics</strong> — Comprehensive reports with PDF/CSV export and scheduled email delivery</li>
          </ul>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="logging-in" title="2. Logging In">
        <p className="text-sm text-slate-600 mb-3">
          Eyvo uses a two-step login process to ensure you connect to the correct organisation:
        </p>
        <GuideSubSection title="Step 1: Enter Your Company Code">
          <p className="text-sm text-slate-600 mb-2">
            On the login screen, enter your <strong>Company Code</strong> (also called Tenant ID). This is a unique identifier provided by your organisation when your Eyvo account was created (e.g., <code>eyvo-demo</code>). Click <strong>Continue</strong> to proceed.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Step 2: Enter Your Credentials">
          <p className="text-sm text-slate-600 mb-2">
            Enter your <strong>Email or User ID</strong> and <strong>Password</strong>, then click <strong>Sign In</strong>. If your account supports Google SSO, you may also see a "Sign in with Google" option.
          </p>
          <GuideCallout type="info" title="Session Management">
            <p>Eyvo enforces single-session login. If you are already logged in on another device, you will see a "Session Conflict" message and can choose to force-end the other session.</p>
          </GuideCallout>
          <GuideCallout type="tip" title="Forgot Password?">
            <p>Click "Forgot password?" on the login screen. Enter your email and you will receive a reset link. Password resets are valid for 60 minutes.</p>
          </GuideCallout>
        </GuideSubSection>
        <GuideSubSection title="Login Page Information">
          <p className="text-sm text-slate-600 mb-2">
            The login page footer displays the current Eyvo version number, the date it was published, the database connection status, and the database name. This information is useful for support purposes.
          </p>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="navigation" title="3. Navigating the Application">
        <GuideSubSection title="Sidebar Navigation">
          <p className="text-sm text-slate-600 mb-2">
            The left sidebar is your primary navigation tool. It displays all available modules as a vertical menu. Some items (like "Data Files" and "Account Codes") have expandable sub-menus indicated by a chevron arrow.
          </p>
          <GuideFieldTable fields={[
            { name: 'Dashboard', desc: 'Your personalised home screen showing approvals, tasks, sourcing activity, and KPIs' },
            { name: 'Command Center', desc: 'A tactical board view showing all active documents across modules in a Kanban-style layout' },
            { name: 'Data Files', desc: 'Master data management — categories, currencies, suppliers, items, delivery points, etc.' },
            { name: 'Account Codes', desc: 'Chart of accounts / expenditure code structure used for budget allocation' },
            { name: 'Contracts', desc: 'Supplier contract management with workflow, clauses, and document attachments' },
            { name: 'Requisitions', desc: 'Purchase requisition creation, editing, and approval routing' },
            { name: 'RFx Sourcing', desc: 'Competitive sourcing events — RFP, RFQ, RFI, and reverse auctions' },
            { name: 'Purchase Orders', desc: 'Formal purchase order creation, amendment, and supplier dispatch' },
            { name: 'Sales Orders', desc: 'Outgoing sales order management for revenue-side transactions' },
            { name: 'Approvals', desc: 'Centralised view of all items awaiting your approval across modules' },
            { name: 'Asset Register', desc: 'Fixed asset tracking, depreciation, and lifecycle management' },
            { name: 'Invoice Matching', desc: 'Invoice receipt, three-way matching, and payment processing' },
            { name: 'Inventory Control', desc: 'Stock management, batch/serial tracking, cycle counting, and analytics' },
            { name: 'Receiving', desc: 'Goods receipt notes (GRN) linked to purchase orders' },
            { name: 'Scheduled Reports', desc: 'Configure automated report delivery via email on a recurring schedule' },
            { name: 'Activity Log', desc: 'Audit trail showing all document actions across the system' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Topbar">
          <p className="text-sm text-slate-600 mb-2">
            The top navigation bar contains:
          </p>
          <GuideFieldTable fields={[
            { name: 'Sidebar Toggle', desc: 'The hamburger menu icon (three lines) collapses or expands the sidebar' },
            { name: 'Company Logo & Name', desc: 'Your organisation\'s logo and name, configured in System Settings' },
            { name: 'Search (Ctrl+K)', desc: 'A command palette for quickly navigating to any module, document, or action' },
            { name: 'Timezone Badge', desc: 'Shows the current system timezone abbreviation (e.g., "PDT"). Hover for the full timezone name.' },
            { name: 'Notifications Bell', desc: 'Shows unread notification count. Click to view approval requests, system alerts, and updates.' },
            { name: 'User Menu', desc: 'Your profile avatar and name. Click to access Profile, System Admin, Theme settings, and Sign Out.' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Sidebar Footer">
          <p className="text-sm text-slate-600 mb-2">
            At the bottom of the sidebar, you will see:
          </p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li><strong>System Clock</strong> — A live clock showing the current date and time in your system timezone (e.g., "11 Apr 2026 10:45:13 AM PDT")</li>
            <li><strong>System Currency</strong> — The base currency configured for your organisation (e.g., "USD")</li>
            <li><strong>Database Status</strong> — A green indicator showing the connected database name</li>
          </ul>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="dashboard" title="4. The Dashboard">
        <p className="text-sm text-slate-600 mb-3">
          The Dashboard is your personalised home screen. It has four views selectable via tabs in the top-right corner: <strong>Action</strong>, <strong>Informative</strong>, <strong>Finance</strong>, and <strong>Briefing</strong>. Your last-used view is remembered automatically.
        </p>
        <GuideSubSection title="Action View">
          <p className="text-sm text-slate-600 mb-2">The default view focused on items requiring your attention. A graphical hero strip sits at the top with sparkline-backed KPI tiles, followed by the operational work area:</p>
          <GuideFieldTable fields={[
            { name: 'Hero KPI Strip', desc: 'Gradient tiles for Action items, POs YTD with mini-trend, Approval cycle (avg hours), and Open RFx with contracts-expiring count' },
            { name: 'Top 5 Needing Attention', desc: 'Priority-sorted list of approvals, at-risk items, and live auctions with HIGH/MEDIUM/LOW priority dots' },
            { name: 'KPI Action Cards', desc: 'Approvals Pending, At Risk / Delayed, Active Sourcing, Live Auctions, and Tasks Due — click any card to expand inline detail' },
            { name: 'My Approvals', desc: 'A list of requisitions and purchase orders awaiting your approval, with values and dates' },
            { name: 'My Tasks', desc: 'Quick links to closing sourcing events, live auctions, pending invoices, and delayed requisitions' },
            { name: 'Active Sourcing Events', desc: 'Open RFP/RFQ events with their closing dates and supplier counts' },
            { name: 'Live Activity', desc: 'A real-time feed of recent system events (GRN receipts, PO status changes, etc.)' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Informative View">
          <p className="text-sm text-slate-600 mb-2">
            An executive scorecard answering "what's the state of the business?" — driven entirely by your live data:
          </p>
          <GuideFieldTable fields={[
            { name: 'Headline Tiles', desc: 'Spend over the last 12 months, your top supplier by value, contracts at risk (expiring within 90 days), and open RFx events' },
            { name: 'Spend & Volume — 12 Months', desc: 'Composed chart with an area for total spend and bars for monthly order counts' },
            { name: 'Top Suppliers by Spend', desc: 'Ranked leaderboard with progress bars showing share of spend' },
            { name: 'Contracts Expiring — Next 90 Days', desc: 'Renewal pipeline with days-left badges colour-coded by urgency' },
            { name: 'Spend by Category', desc: 'Horizontal bar chart of your top 8 spend categories' },
            { name: 'Supplier Risk Distribution', desc: 'Donut chart of supplier statuses across your supplier base' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Finance View">
          <p className="text-sm text-slate-600 mb-2">
            A finance-focused view for AP teams and finance managers:
          </p>
          <GuideFieldTable fields={[
            { name: 'Headline Tiles', desc: 'AP outstanding, AP overdue, 12-month spend with sparkline, and average PO value' },
            { name: 'AP Aging', desc: 'Colour-coded bar chart showing balances in Current, 1-30, 31-60, 61-90, and 90+ day buckets' },
            { name: 'Invoice Status Mix', desc: 'Donut chart of invoices by status with counts and amounts in the tooltip' },
            { name: 'Top Vendors by Spend', desc: 'Ranked leaderboard of suppliers over the last 12 months' },
            { name: 'Monthly Spend Trend', desc: '12-month spend area chart for cash-flow tracking' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Briefing View">
          <p className="text-sm text-slate-600 mb-2">
            An executive briefing that summarises the day's most important signals from your live procurement data and recommends where to focus next.
          </p>
          <GuideFieldTable fields={[
            { name: 'Headline', desc: 'A one-sentence summary of the state of the business with concrete numbers (12-month spend, AP overdue, open RFx)' },
            { name: 'Snapshot Tiles', desc: 'Quick-glance KPIs (Spend 12m, Open RFx, AP Overdue, Contracts at Risk) — click any tile to jump to the relevant module' },
            { name: 'Key Insights', desc: 'Three to five numbered bullets with concrete data points (supplier names, dollar values, time periods)' },
            { name: 'Recommended Actions', desc: 'Suggested next steps with a short rationale for each' },
            { name: 'Clickable Drill-Down', desc: 'Every bullet and action is clickable — clicking navigates straight into the relevant module (Orders, Invoices, RFx, Suppliers, Contracts) so you can act immediately' },
            { name: 'Regenerate', desc: 'The briefing is cached for 10 minutes; click Regenerate in the hero to force a fresh narrative against the latest data' },
          ]} />
          <GuideCallout type="tip" title="Treat the Briefing as a starting point">
            <p>The briefing is a narrative summary intended to guide your attention. Always verify the underlying data before acting on financial decisions — every bullet links you to the source records.</p>
          </GuideCallout>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="command-center" title="5. Command Center (ATC)">
        <p className="text-sm text-slate-600 mb-3">
          The Command Center provides a tactical, Kanban-style board view of all active documents across your procurement pipeline. Think of it as an air traffic control tower for your procurement operations.
        </p>
        <p className="text-sm text-slate-600 mb-2">
          Each column represents a document status (Draft, Unapproved, Pending, Approved, etc.) and cards represent individual requisitions, purchase orders, invoices, or sourcing events. Cards are colour-coded by module and show key information like document number, supplier, and value.
        </p>
        <GuideCallout type="tip" title="Real-Time Updates">
          <p>The Command Center refreshes automatically to show the latest document statuses without requiring a page reload.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="data-files" title="6. Data Files (Master Data)">
        <p className="text-sm text-slate-600 mb-3">
          Data Files contain all the reference data that underpins your procurement transactions. This includes suppliers, items, delivery points, currency codes, document types, and more. Each data file type has its own listing page with search, filtering, sorting, and a Reports tab.
        </p>
        <p className="text-sm text-slate-600 mb-2">
          For detailed information on each data file, see the <strong>Data Files Module Guide</strong>.
        </p>
        <GuideSubSection title="Common Features Across All Data Files">
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li><strong>Interactive Table</strong> — All lists use a resizable, sortable table with inline column filters</li>
            <li><strong>Search</strong> — A global search bar filters records by any visible field</li>
            <li><strong>Pagination</strong> — Records are paginated (default 25 per page) with page size options</li>
            <li><strong>Reports Tab</strong> — Each data file has a dedicated Reports tab for advanced filtering, PDF/CSV export</li>
            <li><strong>Show Addresses</strong> — Where applicable, a checkbox toggles visibility of address columns</li>
          </ul>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="account-codes" title="7. Account Codes">
        <p className="text-sm text-slate-600 mb-3">
          Account Codes (also called Expenditure Codes) provide a hierarchical structure for categorising spend. They are used on requisitions and purchase orders to allocate costs to the correct budget or department. For full details, see the <strong>Account Codes Module Guide</strong>.
        </p>
      </GuideSection>

      <GuideSection id="modules-overview" title="8. Core Modules Overview">
        <GuideSubSection title="Contracts">
          <p className="text-sm text-slate-600 mb-2">
            Manage supplier agreements with a full document lifecycle: drafting, internal review, legal review, execution, and renewal. Contracts support rich-text editing, clause libraries, version history, and document attachments. See the <strong>Contracts Module Guide</strong> for details.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Requisitions">
          <p className="text-sm text-slate-600 mb-2">
            Purchase requisitions are the starting point of the procurement cycle. Users create a request with line items specifying what they need to purchase, and the requisition is routed through an approval workflow. Approved requisitions can be converted to purchase orders. See the <strong>Requisitions Module Guide</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="RFx Sourcing">
          <p className="text-sm text-slate-600 mb-2">
            The RFx module supports competitive sourcing: Request for Quotation (RFQ), Request for Proposal (RFP), Request for Information (RFI), and live Reverse Auctions. Suppliers are invited, submit responses, and results are evaluated with award recommendations. See the <strong>RFx Sourcing Module Guide</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Purchase Orders">
          <p className="text-sm text-slate-600 mb-2">
            Purchase Orders (POs) are formal documents sent to suppliers authorising a purchase. They flow through approval workflows, can be amended, dispatched to suppliers, and are linked to goods receipts and invoices. See the <strong>Purchase Orders Module Guide</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Asset Register">
          <p className="text-sm text-slate-600 mb-2">
            Track fixed assets from acquisition through depreciation to disposal. Supports multiple depreciation methods (straight-line, declining balance, units-of-production), asset categories, and full audit trails. See the <strong>Asset Register Module Guide</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Invoice Matching">
          <p className="text-sm text-slate-600 mb-2">
            The invoicing module handles three-way matching (invoice vs. order vs. goods receipt), tolerance-based auto-matching, email ingestion of supplier invoices, and payment tracking. See the <strong>Invoice Matching Module Guide</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Inventory Control">
          <p className="text-sm text-slate-600 mb-2">
            Full inventory management including stock levels by location, batch and serial number tracking, cycle counting, stock movements, replenishment rules, and comprehensive analytics. See the <strong>Inventory Control Module Guide</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Receiving">
          <p className="text-sm text-slate-600 mb-2">
            Record the receipt of goods against purchase orders. Create Goods Received Notes (GRNs) specifying quantities received, discrepancies, and delivery notes. See the <strong>Receiving Module Guide</strong>.
          </p>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="reports" title="9. Reporting & Exports">
        <p className="text-sm text-slate-600 mb-3">
          Eyvo provides comprehensive reporting across all modules. Reports can be accessed in two ways:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 mb-3">
          <li><strong>Module Reports Tab</strong> — Each module's listing page has a "Reports" tab with specialised reports for that module</li>
          <li><strong>Scheduled Reports</strong> — Configure reports to run automatically and be delivered via email on a daily, weekly, or monthly schedule</li>
        </ul>
        <GuideSubSection title="Export Options">
          <GuideFieldTable fields={[
            { name: 'CSV Export', desc: 'Download the full dataset as a comma-separated values file, including all database columns' },
            { name: 'PDF Export', desc: 'Generate a professionally formatted PDF with Eyvo branding, suitable for distribution' },
            { name: 'Email PDF', desc: 'Generate a PDF and email it directly to colleagues or stakeholders' },
            { name: 'Print', desc: 'Open a print-friendly version in a new window for direct printing' },
          ]} />
        </GuideSubSection>
        <p className="text-sm text-slate-600">
          For full details, see the <strong>Reports System Guide</strong> and <strong>Scheduled Reports Guide</strong>.
        </p>
      </GuideSection>

      <GuideSection id="system-admin" title="10. System Administration">
        <p className="text-sm text-slate-600 mb-3">
          System Administration (accessible via the user menu for admin users) provides controls for:
        </p>
        <GuideFieldTable fields={[
          { name: 'User Register', desc: 'Manage user accounts, roles, permissions, and licence allocation' },
          { name: 'System Settings', desc: 'Configure company details, base currency, date format, timezone, and UI preferences' },
          { name: 'Approval Rules', desc: 'Set up multi-level approval workflows based on document value, type, or department' },
          { name: 'Account Code Setup', desc: 'Define the structure of your expenditure code hierarchy' },
          { name: 'Contract Settings', desc: 'Configure clause libraries and contract workflow stages' },
          { name: 'Auth Settings', desc: 'Configure password policies, TOTP two-factor authentication, and brute-force protection' },
          { name: 'Version History', desc: 'View the application version and release notes' },
          { name: 'Data Management', desc: 'Archive or purge old transaction data' },
        ]} />
      </GuideSection>

      <GuideSection id="timezone" title="11. Timezone & Date Display">
        <p className="text-sm text-slate-600 mb-3">
          All dates and times in Eyvo are displayed in your organisation's configured timezone. The timezone is set by your system administrator in <strong>System Administration &gt; System Settings &gt; Timezone</strong>.
        </p>
        <p className="text-sm text-slate-600 mb-2">
          The current timezone is always visible in two places:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>The <strong>timezone badge</strong> in the top navigation bar (e.g., "PDT")</li>
          <li>The <strong>system clock</strong> in the sidebar footer (e.g., "11 Apr 2026 10:45:13 AM PDT")</li>
        </ul>
        <GuideCallout type="info" title="Consistent Timestamps">
          <p>Whether you are in London, Singapore, or California, Eyvo displays all timestamps in the same organisation timezone so that all users see the same date and time for every record.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="accessibility" title="12. Accessibility">
        <p className="text-sm text-slate-600 mb-3">
          Eyvo is designed to meet WCAG 2.1 AA accessibility standards. Features include:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Full keyboard navigation across all modules</li>
          <li>ARIA labels on interactive elements for screen reader compatibility</li>
          <li>An accessibility widget (bottom-right corner) for adjusting text size, contrast, and motion</li>
          <li>Colour contrast ratios meeting AA requirements</li>
          <li>Skip-to-content links for efficient keyboard navigation</li>
        </ul>
      </GuideSection>

      <GuideSection id="support" title="13. Getting Help">
        <p className="text-sm text-slate-600 mb-3">
          Every module page in Eyvo has a <strong>help icon</strong> (question mark) that opens the relevant user guide directly within the application. You can also:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Click <strong>"Open Full Page"</strong> to view the guide in a dedicated page</li>
          <li>Click <strong>"Print / PDF"</strong> at the top of any guide to generate a printable/PDF version</li>
          <li>Contact your system administrator for organisation-specific questions</li>
          <li>Visit <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.eyvo.com</a> for product information and support</li>
        </ul>
      </GuideSection>
    </GuideViewer>
  );
}

export default function GeneralSystemGuidePage() {
  return (
    <AppShell>
      <GuideContent />
    </AppShell>
  );
}

export function GeneralSystemGuideEmbed() {
  return <GuideContent />;
}
